import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Camera, Video, Mic, Image, Globe, ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { services } from '../data/services';
import { galleryImages } from '../data/gallery';

const heroSlides = [
  { src: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/prq4obov__DSC4113.jpg', tagline: 'Révéler la vie cachée de nos rivières' },
  { src: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/si0g6wgm__DSC3570.jpg', tagline: 'Là où la lumière danse sous l\'eau' },
  { src: 'https://customer-assets.emergentagent.com/job_template-astud/artifacts/n2bfgxlk__DSC8423%20%281%29.jpg', tagline: 'La biodiversité des eaux douces à portée de regard' },
];

const iconMap = { Camera, Video, Mic, Image, Globe };

const steps = [
  { num: '01', title: 'Premier contact & Briefing', desc: "L'objectif est de définir vos besoins, l'univers de votre marque et les supports de diffusion prévus. Nous échangeons sur vos attentes esthétiques afin d'établir un devis sur mesure." },
  { num: '02', title: 'Organisation & Moodboard', desc: "Une fois le projet validé, nous passons à la phase préparatoire : repérage des lieux, définition du stylisme ou création d'un moodboard précis." },
  { num: '03', title: 'Séance de shooting', desc: "C'est le moment de la création. Je dirige la séance en mettant l'accent sur la lumière et la composition. Mon rôle est de capturer l'essence de votre sujet." },
  { num: '04', title: 'Post-production & Livraison', desc: "Après une sélection rigoureuse, je procède à la retouche numérique. Vos visuels définitifs vous sont livrés en haute définition via une galerie sécurisée.", highlight: true },
];

/* === Animated background elements === */

function Bubbles({ count = 12, color = 'blue' }) {
  const bubbles = Array.from({ length: count }, (_, i) => ({
    id: i,
    size: Math.random() * 12 + 4,
    left: Math.random() * 100,
    delay: Math.random() * 8,
    duration: Math.random() * 10 + 12,
  }));
  const bg = color === 'green'
    ? 'radial-gradient(circle at 30% 30%, rgba(58,125,92,0.3), rgba(26,92,79,0.1))'
    : 'radial-gradient(circle at 30% 30%, rgba(125,212,245,0.3), rgba(43,123,168,0.1))';
  const border = color === 'green'
    ? '1px solid rgba(58,125,92,0.15)'
    : '1px solid rgba(125,212,245,0.15)';
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map(b => (
        <div key={b.id} className="bubble" style={{ width: b.size, height: b.size, left: `${b.left}%`, bottom: '-20px', animationDelay: `${b.delay}s`, animationDuration: `${b.duration}s`, background: bg, borderColor: border }} />
      ))}
    </div>
  );
}

function RiverParticles({ count = 15 }) {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 1.5,
    left: Math.random() * 100,
    delay: Math.random() * 10,
    duration: Math.random() * 12 + 14,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <div key={p.id} className="river-particle" style={{ width: p.size, height: p.size, left: `${p.left}%`, bottom: '-10px', animationDelay: `${p.delay}s`, animationDuration: `${p.duration}s` }} />
      ))}
    </div>
  );
}

function CurrentLines({ count = 3 }) {
  const lines = Array.from({ length: count }, (_, i) => ({
    id: i,
    top: 20 + Math.random() * 60,
    width: 300 + Math.random() * 500,
    delay: Math.random() * 15,
    duration: 14 + Math.random() * 10,
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {lines.map(l => (
        <div key={l.id} className="current-line" style={{ top: `${l.top}%`, width: l.width, animationDelay: `${l.delay}s`, animationDuration: `${l.duration}s` }} />
      ))}
    </div>
  );
}

function FadeInSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: [0.23, 1, 0.32, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

/* === SECTIONS === */

function HeroSection() {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);

  const resetTimer = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCurrent(c => (c + 1) % heroSlides.length), 5000);
  }, []);

  useEffect(() => { resetTimer(); return () => clearTimeout(timeoutRef.current); }, [current, resetTimer]);
  const goTo = (dir) => setCurrent(c => (c + dir + heroSlides.length) % heroSlides.length);

  return (
    <section data-testid="hero-section" className="relative h-screen overflow-hidden">
      {heroSlides.map((slide, i) => (
        <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === current ? 'opacity-100' : 'opacity-0'}`}>
          <img src={slide.src} alt="" className="w-full h-full object-cover hero-slide-image" />
        </div>
      ))}
      <div className="hero-overlay absolute inset-0" />
      <Bubbles count={10} />
      <div className="caustics-overlay" />

      <div className="absolute inset-0 flex flex-col items-center justify-center text-center z-10 px-6">
        <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-montserrat text-sm md:text-base uppercase tracking-[0.3em] text-[#C4A35A] mb-4 font-semibold">
          Photographe subaquatique en eau douce
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }} className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
          Yannick Gouguenheim
        </motion.h1>
        <div className="animated-line mx-auto mb-6" />
        <motion.p key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }} className="font-playfair text-lg md:text-xl italic text-[#8CA1B0] max-w-xl mb-10">
          "{heroSlides[current].tagline}"
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }} className="flex flex-col sm:flex-row gap-4">
          <Link to="/galerie" data-testid="hero-cta-gallery" className="px-8 py-3.5 rounded-full bg-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:bg-[#5AACE0] transition-all duration-300 hover:shadow-lg hover:shadow-[#2B7BA8]/30">
            Découvrir la galerie
          </Link>
          <Link to="/contact" data-testid="hero-cta-contact" className="px-8 py-3.5 rounded-full border border-white/20 text-white font-montserrat text-sm uppercase tracking-wider hover:border-[#C4A35A] hover:text-[#C4A35A] transition-all duration-300">
            Me contacter
          </Link>
        </motion.div>
      </div>

      <button onClick={() => goTo(-1)} data-testid="hero-prev" className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass flex items-center justify-center text-white/60 hover:text-white transition-colors"><ChevronLeft size={20} /></button>
      <button onClick={() => goTo(1)} data-testid="hero-next" className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full glass flex items-center justify-center text-white/60 hover:text-white transition-colors"><ChevronRight size={20} /></button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-4">
        <div className="flex gap-3">
          {heroSlides.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} data-testid={`hero-dot-${i}`} className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? 'bg-[#C4A35A] w-8' : 'bg-white/30'}`} />
          ))}
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="flex flex-col items-center gap-1 cursor-pointer" onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })} data-testid="scroll-indicator">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-1.5">
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }} className="w-1.5 h-1.5 rounded-full bg-[#C4A35A]" />
          </div>
          <span className="font-montserrat text-[10px] uppercase tracking-widest text-white/30">Scroll</span>
        </motion.div>
      </div>
    </section>
  );
}

function IdentitySection() {
  return (
    <section className="py-24 section-sandy relative overflow-hidden">
      <div className="caustics-overlay" />
      <FadeInSection className="max-w-3xl mx-auto text-center px-6 relative z-10">
        <p className="font-playfair text-2xl md:text-3xl italic text-[#1A2E1F] leading-relaxed mb-6">
          "Plongeur, biologiste, photographe — je capture ce que peu d'yeux ont vu."
        </p>
        <div className="w-16 h-0.5 bg-gradient-to-r from-transparent via-[#2D5A3D] to-transparent mx-auto mb-6" />
        <p className="font-lato text-base md:text-lg text-[#4A4A4A] leading-relaxed">
          Depuis 2009, je documente la biodiversité des milieux aquatiques continentaux
          pour aider les gestionnaires, les collectivités et le grand public
          à mieux comprendre et protéger nos eaux douces.
        </p>
      </FadeInSection>
    </section>
  );
}

function ServicesSection() {
  return (
    <section data-testid="services-section" className="py-24 section-emerald relative overflow-hidden">
      <Bubbles count={6} color="green" />
      <div className="caustics-strong" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeInSection className="text-center mb-16">
          <p className="font-montserrat text-xs uppercase tracking-[0.3em] text-[#3A7D5C] mb-3">Ce que je propose</p>
          <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight">Mes Prestations</h2>
        </FadeInSection>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon];
            return (
              <FadeInSection key={s.id} delay={i * 0.1}>
                <Link to={`/prestations/${s.id}`} data-testid={`service-card-${s.id}`} className="service-card block glass-emerald rounded-2xl p-8 h-full">
                  <div className="w-14 h-14 rounded-xl bg-[#1A5C4F]/25 flex items-center justify-center mb-5">
                    <Icon size={24} className="text-[#3A7D5C]" />
                  </div>
                  <h3 className="font-montserrat text-lg font-semibold text-white mb-3">{s.title}</h3>
                  <p className="font-lato text-sm text-[#8CA1B0] leading-relaxed mb-5">{s.shortDesc}</p>
                  <span className="font-montserrat text-sm text-[#C4A35A] inline-flex items-center gap-2 group">
                    En savoir plus <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </FadeInSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function CTABanner() {
  return (
    <section
      className="py-20 cta-parallax relative overflow-hidden"
      style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_template-astud/artifacts/si0g6wgm__DSC3570.jpg')` }}
    >
      <div className="caustics-overlay" style={{ zIndex: 2 }} />
      <FadeInSection className="max-w-3xl mx-auto text-center px-6 relative z-10">
        <h2 className="font-playfair text-3xl md:text-4xl text-white mb-6">Vous avez un projet ?</h2>
        <p className="font-lato text-lg text-[#8CA1B0] mb-8">Parlons-en ensemble.</p>
        <Link to="/contact" data-testid="cta-banner-contact" className="inline-block px-8 py-3.5 rounded-full border-2 border-[#C4A35A]/50 text-[#C4A35A] font-montserrat text-sm uppercase tracking-wider hover:bg-[#C4A35A] hover:text-[#0B1D15] transition-all duration-400">
          Contactez-moi <ArrowRight size={14} className="inline ml-2" />
        </Link>
      </FadeInSection>
    </section>
  );
}

function HowToWork() {
  const step01 = steps[0];
  const stepsBottom = steps.slice(1);
  return (
    <section className="py-24 section-stone relative overflow-hidden" data-testid="how-to-work-section">
      <div className="caustics-overlay" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeInSection className="text-center mb-12">
          <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight">Comment travailler avec moi ?</h2>
          <p className="font-playfair italic text-[#8CA1B0] mt-3">Les grandes étapes</p>
        </FadeInSection>

        {/* Row 1 : Photo large + Step 01 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
          <FadeInSection className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden h-full">
              <img
                src="https://customer-assets.emergentagent.com/job_template-astud/artifacts/p2firt48_Auteur-2-scaled.webp"
                alt="Yannick Gouguenheim en plongee"
                className="w-full h-full object-cover min-h-[300px] max-h-[380px]"
              />
            </div>
          </FadeInSection>

          <FadeInSection delay={0.1}>
            <div className="relative rounded-2xl p-7 h-full glass-light flex flex-col justify-center">
              <span className="step-number" style={{ fontSize: '3.5rem' }}>{step01.num}</span>
              <div className="relative z-10">
                <h3 className="font-montserrat text-base font-semibold text-[#3A7D5C] mb-3">{step01.title}</h3>
                <p className="font-lato text-sm text-[#8CA1B0] leading-relaxed">{step01.desc}</p>
              </div>
            </div>
          </FadeInSection>
        </div>

        {/* Row 2 : Steps 02, 03, 04 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {stepsBottom.map((step, i) => (
            <FadeInSection key={i} delay={i * 0.1}>
              <div className={`relative rounded-2xl p-7 h-full ${step.highlight ? 'bg-gradient-to-br from-[#1A5C4F] to-[#2B7BA8] text-white' : 'glass-light'}`}>
                <span className="step-number">{step.num}</span>
                <div className="relative z-10">
                  <h3 className={`font-montserrat text-base font-semibold mb-3 ${step.highlight ? 'text-[#C4A35A]' : 'text-[#3A7D5C]'}`}>
                    {step.title}
                  </h3>
                  <p className={`font-lato text-sm leading-relaxed ${step.highlight ? 'text-white/90' : 'text-[#8CA1B0]'}`}>
                    {step.desc}
                  </p>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryTeaser() {
  const previewImages = galleryImages.slice(0, 6);
  return (
    <section className="py-24 section-riverbed relative overflow-hidden" data-testid="gallery-teaser-section">
      <Bubbles count={5} color="green" />
      <div className="caustics-strong" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <FadeInSection className="text-center mb-16">
          <p className="font-montserrat text-xs uppercase tracking-[0.3em] text-[#C4A35A] mb-3">Portfolio</p>
          <h2 className="font-playfair text-4xl md:text-5xl text-white tracking-tight">Quelques regards sous la surface</h2>
        </FadeInSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {previewImages.map((img, i) => (
            <FadeInSection key={img.id} delay={i * 0.08}>
              <div className="gallery-item rounded-2xl overflow-hidden aspect-[4/3]">
                <img src={img.src} alt={img.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="gallery-overlay rounded-2xl items-center justify-center">
                  <Search size={20} className="text-[#C4A35A]" />
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>

        <FadeInSection className="text-center mt-12">
          <Link to="/galerie" data-testid="gallery-teaser-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#1A5C4F] to-[#2B7BA8] text-white font-montserrat text-sm uppercase tracking-wider hover:shadow-lg hover:shadow-[#1A5C4F]/30 transition-all duration-300">
            Voir toute la galerie <ArrowRight size={14} />
          </Link>
        </FadeInSection>
      </div>
    </section>
  );
}

function CTAFinal() {
  return (
    <section
      className="py-24 cta-parallax relative overflow-hidden"
      style={{ backgroundImage: `url('https://customer-assets.emergentagent.com/job_template-astud/artifacts/9gxho4db__DSC7031.jpg')` }}
    >
      <div className="caustics-overlay" style={{ zIndex: 2 }} />
      <Bubbles count={6} />
      <FadeInSection className="max-w-3xl mx-auto text-center px-6 relative z-10">
        <h2 className="font-playfair text-3xl md:text-4xl text-white mb-4">
          Un projet de communication autour des milieux aquatiques ?
        </h2>
        <p className="font-lato text-lg text-white/70 mb-8">
          Je serais ravi d'en discuter avec vous.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" data-testid="cta-final-contact" className="px-8 py-3.5 rounded-full bg-[#C4A35A] text-[#0B1D15] font-montserrat text-sm uppercase tracking-wider hover:bg-[#D4B76A] transition-all duration-300 font-semibold">
            Prendre contact
          </Link>
          <a href="tel:+33647786790" className="px-8 py-3.5 rounded-full border-2 border-white/30 text-white font-montserrat text-sm uppercase tracking-wider hover:bg-white/10 transition-all duration-300">
            06.47.78.67.90
          </a>
        </div>
      </FadeInSection>
    </section>
  );
}

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <IdentitySection />
      <ServicesSection />
      <CTABanner />
      <HowToWork />
      <GalleryTeaser />
      <CTAFinal />
    </main>
  );
}
